import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { programsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, FolderKanban } from 'lucide-react';
import { toast } from 'sonner';

export default function ProgramListPage() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { loadPrograms(); }, [statusFilter]);

  const loadPrograms = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await programsAPI.list(params);
      setPrograms(res.data);
    } catch { toast.error('Failed to load programs'); }
    finally { setLoading(false); }
  };

  const filtered = programs.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.program_name?.toLowerCase().includes(q) ||
           p.program_number?.toLowerCase().includes(q) ||
           p.customer_name?.toLowerCase().includes(q);
  });

  const STATUS_COLORS = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
    completed: 'bg-slate-50 text-slate-600 border-slate-200',
    on_hold: 'bg-amber-50 text-amber-700 border-amber-200',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="program-list-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-bold tracking-tight">Programs</h1>
        <Button size="sm" onClick={() => navigate('/programs/new')} data-testid="new-program-btn">
          <Plus className="w-4 h-4 mr-1" /> New Program
        </Button>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-8 h-9 text-xs" placeholder="Search programs..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderKanban className="w-12 h-12 text-muted-foreground/30 mb-2" />
            <p className="text-sm text-muted-foreground">No programs found</p>
            <Button size="sm" className="mt-3" onClick={() => navigate('/programs/new')}>Create Program</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => (
            <Card key={p.id} className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/programs/${p.id}`)} data-testid={`program-card-${p.id}`}>
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{p.program_name}</p>
                      <span className="text-xs font-mono text-muted-foreground">{p.program_number}</span>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[p.status] || ''}`}>{p.status?.replace(/_/g, ' ')}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Customer: {p.customer_name} | POs: {p.purchase_order_ids?.length || 0} | Items: {p.items?.length || 0} | Tasks: {p.tasks?.length || 0}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">{p.created_at?.slice(0, 10)}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
